import { ComponentProps } from 'react'

import { DataGrid } from '@mui/x-data-grid'

import { usePaginationDisplayText } from 'app/lib/hooks/use-pagination-display-text'

export interface BasePaginatedTableProps
  extends Pick<
    ComponentProps<typeof DataGrid>,
    | 'rows'
    | 'columns'
    | 'loading'
    | 'onRowClick'
    | 'paginationModel'
    | 'onPaginationModelChange'
    | 'rowCount'
    | 'onRowCountChange'
    | 'paginationMeta'
    | 'pageSizeOptions'
    | 'paginationMode'
  > {
  paginationDisplayText?: (args: { from: number; to: number; count: number }) => string
  hideColumnHeaders?: boolean
  testID?: string
}

export const TABLE_STYLES = {
  border: 'none',
  backgroundColor: 'transparent',
  display: 'grid',
  gridTemplateRows: 'auto 1f auto',
  '& .MuiDataGrid-main': {
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: 'none',
      pointerEvents: 'none',
      '& .MuiDataGrid-columnHeader': {
        backgroundColor: 'rgb(244, 244, 244)',
        borderBottom: 'none',
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.6)',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.08333em',
          whiteSpace: 'normal',
        },
      },
      '& .MuiDataGrid-columnSeparator': {
        display: 'none',
      },
    },
    '& .MuiDataGrid-virtualScrollerContent': {
      '& .MuiDataGrid-row': {
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'rgba(224, 224, 224, 0.3)',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'rgb(255, 239, 226)',
        },
        '&:first-of-type': {
          borderTop: 'none',
        },
        '&:last-child': {
          borderBottom: 'none',
        },
      },
      '& .MuiDataGrid-cell': {
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        lineHeight: 1.43,
      },
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: 'none',
    justifyContent: 'center',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
}

export const BasePaginatedTable = ({
  rows,
  columns,
  loading = false,
  onRowClick,
  testID = 'paginated-table',
  paginationModel,
  onPaginationModelChange,
  rowCount,
  onRowCountChange,
  paginationMeta,
  paginationDisplayText,
  pageSizeOptions,
  hideColumnHeaders = false,
}: BasePaginatedTableProps) => {
  const defaultPaginationDisplayText = usePaginationDisplayText({ countUnknown: false })

  return (
    <div style={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        sx={[
          TABLE_STYLES,
          hideColumnHeaders && { '& .MuiDataGrid-columnHeaders': { display: 'none' } },
        ]}
        columnHeaderHeight={hideColumnHeaders ? 0 : undefined}
        onRowClick={onRowClick}
        disableColumnMenu={true}
        disableColumnSorting={true}
        rowCount={rowCount}
        onRowCountChange={onRowCountChange}
        pageSizeOptions={pageSizeOptions}
        paginationMeta={paginationMeta}
        paginationMode="server"
        onPaginationModelChange={onPaginationModelChange}
        paginationModel={paginationModel}
        loading={loading}
        localeText={{
          paginationDisplayedRows: paginationDisplayText || defaultPaginationDisplayText,
        }}
        slotProps={{
          root: {
            'data-testid': testID,
          },
        }}
      />
    </div>
  )
}
